import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, TrendingUp, Download, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    fetchStoresWhereStaffAssigned,
    fetchUtilCatentriesForStore,
    fetchInventoryClosingReport,
    fetchClosingInventoryExportCSV
} from '../apiHelper';
import Card from '../components/ui/Card';
import { useToast } from '../hooks/useToast';

const formatDate = (date) => date.toISOString().split('T')[0];

const InventoryClosingReport = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [range, setRange] = useState({
        start_date: formatDate(new Date(new Date().setDate(new Date().getDate() - 30))),
        end_date: formatDate(new Date())
    });

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) navigate('/login');
        else setUser(JSON.parse(lcUser));
    }, [navigate]);

    useEffect(() => {
        if (user) {
            fetchStoresWhereStaffAssigned(user).then(allStores => {
                setStores(allStores);
                if (allStores.length > 0) setSelectedStore(allStores[0]);
            });
        }
    }, [user]);

    useEffect(() => {
        if (selectedStore) {
            fetchUtilCatentriesForStore({ store_id: selectedStore.store_id })
                .then(items => {
                    const formatted = items.map(item => ({
                        label: item.productname,
                        value: item.catentry_id,
                    }));
                    setProducts(formatted);
                    if (formatted.length > 0) setSelectedProduct(formatted[0].value);
                    else setSelectedProduct(null);
                });
        }
    }, [selectedStore]);

    const loadReport = async () => {
        if (!selectedStore || !selectedProduct) return;
        setLoading(true);
        try {
            const data = await fetchInventoryClosingReport({
                ...range,
                store_id: selectedStore.store_id,
                catentry_id: selectedProduct
            });
            setReportData(data);
        } catch (err) {
            showToast("Failed to load inventory report", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadReport(); }, [range, selectedStore, selectedProduct]);

    const handleExportCSV = async () => {
        if (!selectedStore || !selectedProduct) return;
        try {
            const csvContent = await fetchClosingInventoryExportCSV({
                store_id: selectedStore.store_id,
                catentry_id: selectedProduct,
                ...range
            });

            // Create blob and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Inventory_Report_${selectedStore.identifier}_${range.start_date}_to_${range.end_date}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showToast("Failed to export CSV", "error");
        }
    };

    const generatePDF = () => {
        if (!reportData || !reportData.data) return;

        const doc = new jsPDF('l', 'mm', 'a4');
        const productName = products.find(p => p.value == selectedProduct)?.label;

        // 1. Report Title
        doc.setFontSize(22);
        doc.setTextColor(31, 41, 55);
        doc.text("Inventory Closing & Valuation Report", 14, 20);

        // 2. Metadata Section
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Store: ${selectedStore.identifier}`, 14, 30);
        doc.text(`Product: ${productName}`, 14, 35);
        doc.text(`Date Range: ${range.start_date} to ${range.end_date}`, 14, 40);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 45);

        // 3. Summary Table
        autoTable(doc, {
            startY: 55,
            head: [['Key Metric', 'Value']],
            body: [
                ['Final Valuation', `₦${Number(reportData.final_valuation).toLocaleString()}`],
                ['Final Stock Level', reportData.final_stock_level]
            ],
            theme: 'striped',
            headStyles: { fillColor: [79, 70, 229] },
            columnStyles: { 0: { fontStyle: 'bold' } },
            margin: { right: 150 }
        });

        // 4. Detailed Data Table
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 10,
            head: [['Date', 'Type', 'Product', 'Part #', 'Lot ID', 'Stock', 'Unit Cost', 'Valuation', 'WAC']],
            body: reportData.data.map(r => [
                new Date(r.date).toLocaleDateString(),
                r.type,
                r.product_name,
                r.partnumber,
                r.lot_id,
                r.cumulative_stock,
                Number(r.unit_cost).toFixed(2),
                Number(r.inventory_valuation).toLocaleString(),
                Number(r.weighted_avg_cost).toFixed(2)
            ]),
            headStyles: { fillColor: [31, 41, 55] },
            styles: { fontSize: 8 }
        });

        doc.save(`Inventory_Report_${selectedStore.identifier}_${productName}.pdf`);
    };

    return (
        <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
            <div className="mb-6 flex flex-col gap-4">
                <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
                    <TrendingUp className="text-indigo-600" size={24} /> Inventory Closing & Valuation
                </h1>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-center gap-2 bg-white p-2 rounded-lg border shadow-sm">
                    <select className="text-xs font-bold p-2 border-b sm:border-b-0 sm:border-r w-full lg:w-auto" value={selectedStore?.store_id || ''} onChange={(e) => setSelectedStore(stores.find(s => s.store_id == e.target.value))}>
                        {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.identifier}</option>)}
                    </select>
                    <select className="text-xs font-bold p-2 border-b sm:border-b-0 sm:border-r w-full lg:w-auto" value={selectedProduct || ''} onChange={(e) => setSelectedProduct(e.target.value)}>
                        {products.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <div className="flex gap-2 w-full lg:w-auto">
                        <input type="date" value={range.start_date} onChange={e => setRange({ ...range, start_date: e.target.value })} className="text-xs p-2 border rounded w-full" />
                        <input type="date" value={range.end_date} onChange={e => setRange({ ...range, end_date: e.target.value })} className="text-xs p-2 border rounded w-full" />
                    </div>

                    {/* Added lg:ml-auto here to push this specific div to the far right on desktop */}
                    <div className="flex gap-2 w-full lg:w-auto lg:ml-auto">
                        <button onClick={handleExportCSV} disabled={!reportData} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold w-full justify-center disabled:opacity-50">
                            <Download size={14} /> CSV
                        </button>
                        <button onClick={generatePDF} disabled={!reportData} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold w-full justify-center disabled:opacity-50">
                            <FileText size={14} /> PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* ... rest of the component (Summary Cards and Table view) remains the same */}
            {reportData && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <p className="text-[10px] uppercase text-gray-500 font-bold">Valuation</p>
                        <p className="text-sm sm:text-lg font-black text-indigo-600 truncate">₦{Number(reportData.final_valuation).toLocaleString()}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <p className="text-[10px] uppercase text-gray-500 font-bold">Stock Level</p>
                        <p className="text-sm sm:text-lg font-black text-gray-900 truncate">{reportData.final_stock_level}</p>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" /></div>
            ) : reportData && reportData.data && reportData.data.length > 0 ? (
                <>
                    <Card className="hidden md:block overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs text-left">
                                <thead className="bg-gray-800 text-white">
                                    <tr>
                                        <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Product</th>
                                        <th className="p-3">Part #</th><th className="p-3">Lot</th><th className="p-3 text-right">Stock</th>
                                        <th className="p-3 text-right">Cost</th><th className="p-3 text-right">Valuation</th><th className="p-3 text-right">WAC</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.data.map((row, i) => (
                                        <tr key={i} className="hover:bg-gray-50 border-t">
                                            <td className="p-3">{new Date(row.date).toLocaleDateString()}</td>
                                            <td className="p-3">{row.type}</td>
                                            <td className="p-3 font-bold">{row.product_name}</td>
                                            <td className="p-3 text-gray-500">{row.partnumber}</td>
                                            <td className="p-3">{row.lot_id}</td>
                                            <td className="p-3 text-right">{row.cumulative_stock}</td>
                                            <td className="p-3 text-right">{Number(row.unit_cost).toFixed(2)}</td>
                                            <td className="p-3 text-right font-bold">₦{Number(row.inventory_valuation).toLocaleString()}</td>
                                            <td className="p-3 text-right text-indigo-600 font-bold">{Number(row.weighted_avg_cost).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    <div className="md:hidden space-y-3">
                        {reportData.data.map((row, i) => (
                            <div key={i} className="bg-white p-4 rounded-xl border shadow-sm space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-bold text-gray-900 text-sm">{row.product_name}</p>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase">{row.partnumber} • Lot {row.lot_id}</p>
                                    </div>
                                    <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-[10px] font-black">{row.type}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                                    <div><p className="text-[9px] text-gray-400 uppercase">Stock</p><p className="font-bold text-xs">{row.cumulative_stock}</p></div>
                                    <div><p className="text-[9px] text-gray-400 uppercase">Valuation</p><p className="font-bold text-xs text-indigo-600">₦{Number(row.inventory_valuation).toLocaleString()}</p></div>
                                    <div className="text-right"><p className="text-[9px] text-gray-400 uppercase">WAC</p><p className="font-bold text-xs">{Number(row.weighted_avg_cost).toFixed(2)}</p></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="py-20 text-center text-gray-400 italic">No records found.</div>
            )}
        </div>
    );
};

export default InventoryClosingReport;